import { clsx } from 'clsx'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Outlet, useLocation, useMatch, useParams } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'
import { LeaveModalProvider } from '@/context/LeaveModalContext'
import { useIsMobile } from '@/hooks/useIsMobile'
import { usePublishedElementHeight } from '@/hooks/usePublishedElementHeight'
import { AppHeaderBar } from '@/layouts/AppHeaderBar'
import type { AppHeaderControls } from '@/layouts/header'
import { Sidebar } from '@/layouts/Sidebar'
import { B2bSidebar } from '@/layouts/Sidebar/B2bSidebar'
import { SidebarMobile } from '@/layouts/Sidebar/SidebarMobile'

import '@/layouts/index.scss'
import { isB2bPortalPath, ROUTES } from '@/routes/paths'
import { appSidebarStatus } from '@/services/sidebarStatus.service'

/** The app bar's measured height, for pages that size themselves against it. */
const APP_HEADER_HEIGHT_VAR = '--app-header-height'

export default function AppLayout() {
  const { id: organizationId } = useParams<{ id: string }>()
  const { user, logout } = useAuth()
  const { pathname } = useLocation()
  const handleLogout = async () => await logout()
  // One layout for both portals; the sidebar's contents and data are the portal's own.
  const isB2bPortal = isB2bPortalPath(pathname)

  // Match the scenario/session-feedback subtree so the layout background is dark
  // during the entire roleplay flow — including the brief unmount/remount gap
  // between SimulationFeedback's loading view and SessionFeedback, which would
  // otherwise expose the layout's default white background as a spark.
  // The B2B simulation page is the same dark roleplay surface, outside any workspace.
  const isScenarioRoute = !!useMatch('/workspaces/:id/scenario/:scenarioId/*')
  const isB2bSimulationRoute = !!useMatch(`${ROUTES.B2B.SIMULATION.fullPath}/*`)
  const isDarkRoute = isScenarioRoute || isB2bSimulationRoute

  const [headerNode, setHeaderNode] = useState<ReactNode>(null)
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [sidebarStatus, setSidebarStatus] = useState(() => appSidebarStatus.get())

  // Below the breakpoint the sidebar becomes an off-canvas drawer — see
  // `Sidebar/SidebarMobile`, which owns the scrim and the dismiss behaviours. The
  // open flag lives here rather than in the drawer because the app bar's trigger
  // needs it too, for its own `aria-expanded`.
  const isMobile = useIsMobile()
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)

  const showsMobileDrawer = isMobile && sidebarVisible && !!user

  // Persist sidebar status change
  useEffect(() => {
    appSidebarStatus.set(sidebarStatus)
  }, [sidebarStatus])

  // Crossing back above the breakpoint (rotate, resize) must not leave the drawer
  // flag set, or a later return to mobile would render the drawer already open.
  useEffect(() => {
    if (!isMobile) setIsMobileDrawerOpen(false)
  }, [isMobile])

  const controls: AppHeaderControls = useMemo(
    () => ({
      setHeader: setHeaderNode,
      clearHeader: () => setHeaderNode(null),
      setSidebarVisible,
      setSidebarStatus,
    }),
    [],
  )

  // The app bar renders whenever a page supplies header content, and also on mobile
  // whenever the drawer trigger needs a home — otherwise a page that sets no header
  // would strand a mobile user with no way to reach navigation.
  const showsHeaderBar = !!headerNode || showsMobileDrawer

  // Publish the app bar's real height as `--app-header-height` so pages can size
  // themselves against it.
  //
  // Pages that fill the screen under the header used to hard-code
  // `calc(100dvh - $mobile-header-height)`. The bar is a fixed height per breakpoint
  // (56px mobile, 64px desktop — `.app-layout__header` pins both) and 0 when a page
  // supplies no header, so that was right at one width only. Measuring gives every
  // page the value that applies without each one re-deriving it.
  //
  // Zero rather than nothing when there is no bar: every consumer subtracts this, so
  // an absent property would collapse the `calc()` rather than leave the page full height.
  // A page swapping its header content needs no re-keying — the element stays, and the
  // hook's observer sees the height change.
  const { hostRef: layoutRef, targetRef: headerRef } = usePublishedElementHeight(
    APP_HEADER_HEIGHT_VAR,
    showsHeaderBar,
    { whenTargetAbsent: 'publish-zero' },
  )

  const renderSidebar = (signedInEmail: string, canUseStaffConsole: boolean, isOpen: boolean, onToggle?: () => void) =>
    isB2bPortal ? (
      <B2bSidebar
        userEmail={signedInEmail}
        isOpen={isOpen}
        onToggle={onToggle}
        onLogout={handleLogout}
        showsStaffEntry={canUseStaffConsole}
      />
    ) : (
      <Sidebar
        userEmail={signedInEmail}
        organizationId={organizationId || ''}
        isOpen={isOpen}
        onToggle={onToggle}
        onLogout={handleLogout}
      />
    )

  return (
    <LeaveModalProvider>
      <div ref={layoutRef} className={clsx('app-layout', isDarkRoute && 'app-layout--dark')}>
        {sidebarVisible &&
          user &&
          (showsMobileDrawer ? (
            <SidebarMobile isOpen={isMobileDrawerOpen} onClose={() => setIsMobileDrawerOpen(false)}>
              {/* No collapse chevron on mobile: the app-bar trigger and the scrim are its only controls. */}
              {renderSidebar(
                user.user.email,
                user.my_b2b_platform_capabilities.can_use_staff_console,
                isMobileDrawerOpen,
              )}
            </SidebarMobile>
          ) : (
            renderSidebar(user.user.email, user.my_b2b_platform_capabilities.can_use_staff_console, sidebarStatus, () =>
              setSidebarStatus(prev => !prev),
            )
          ))}

        <div className="app-layout__main">
          {showsHeaderBar ? (
            <AppHeaderBar
              ref={headerRef}
              showsDrawerTrigger={showsMobileDrawer}
              isDrawerOpen={isMobileDrawerOpen}
              onToggleDrawer={() => setIsMobileDrawerOpen(prev => !prev)}
            >
              {headerNode}
            </AppHeaderBar>
          ) : null}
          <div className="app-layout__wrapper">
            <Outlet context={controls} />
          </div>
        </div>
      </div>
    </LeaveModalProvider>
  )
}
